import React, { useCallback } from 'react';
import useApps from './useApps';
import isDefined from '../../../../isDefined';

/**
 * This is the latest definition of Apps,
 * which is different from what we see on {@link PerformanceAppsIndex}
 *
 * We use these to power V3
 */
export const V3AppsContext = React.createContext<{
  availableProducts: V3.Product[];
  availableProductApps: V3.ProductApp[];
  availableWorkspaces: V3.Workspace[];
  allProducts: V3.Product[];
  allProductApps: V3.ProductApp[];
  allWorkspaces: V3.Workspace[];
  reservedWorkspaceUrlSlugs: string[];
  reservedProductUrlSlugs: string[];
  reservedProductAppUrlSlugs: string[];
  getProductAppsUserHasAccessTo: (product: V3.Product) => V3.ProductApp[];
  isLoadingApps: boolean;
  hasAppsEnabled: boolean;
}>({
  availableProducts: [],
  availableProductApps: [],
  availableWorkspaces: [],
  allProducts: [],
  allProductApps: [],
  allWorkspaces: [],
  reservedWorkspaceUrlSlugs: [],
  reservedProductUrlSlugs: [],
  reservedProductAppUrlSlugs: [],
  getProductAppsUserHasAccessTo: () => [],
  isLoadingApps: true,
  hasAppsEnabled: false,
});

export const DEFAULT_V3_SELECTED_APP_CONTEXT = {
  selectedProduct: undefined,
  selectedProductApp: undefined,
  selectedWorkspace: undefined,
};

export const V3SelectedAppContext = React.createContext<{
  selectedProduct?: V3.Product;
  selectedProductApp?: V3.ProductApp;
  selectedWorkspace?: V3.Workspace;
}>(DEFAULT_V3_SELECTED_APP_CONTEXT);

const AppsProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const {
    availableProducts,
    availableProductApps,
    availableWorkspaces,
    allWorkspaces,
    allProducts,
    allProductApps,
    reservedWorkspaceUrlSlugs,
    reservedProductUrlSlugs,
    reservedProductAppUrlSlugs,
    isLoading,
    hasAppsEnabled,
  } = useApps();

  const getProductAppsUserHasAccessTo = useCallback(
    (product: V3.Product) => {
      return product.appIds
        .map((appId) => availableProductApps.find((a) => a.id === appId))
        .filter(isDefined);
    },
    [availableProductApps],
  );

  return (
    <V3AppsContext.Provider
      value={{
        availableProducts,
        availableProductApps,
        availableWorkspaces,
        allProducts,
        allProductApps,
        allWorkspaces,
        reservedWorkspaceUrlSlugs,
        reservedProductUrlSlugs,
        reservedProductAppUrlSlugs,
        getProductAppsUserHasAccessTo,
        isLoadingApps: isLoading,
        hasAppsEnabled,
      }}
    >
      {children}
    </V3AppsContext.Provider>
  );
};

export default AppsProvider;
